import { useState } from 'react';
import {
  FiAlertTriangle as AlertTriangle,
  FiExternalLink as ExternalLink,
  FiInfo as Info,
} from 'react-icons/fi';
import type { ProviderInstructionsConfig } from './types';
import PROVIDERS from './providers';

// Provider Instructions component (collapsible)
export const ProviderInstructions: React.FC<{ provider: string; authMethod?: string }> = ({ provider, authMethod }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const config = PROVIDERS[provider];
  if (!config?.instructions) return null;

  // For providers with multiple auth methods (bedrock, sagemaker), get the specific auth method instructions
  const instructions = config.authMethods
    ? (config.instructions as Record<string, ProviderInstructionsConfig>)[authMethod || '']
    : config.instructions as ProviderInstructionsConfig;

  if (!instructions) return null;

  return (
    <div className="mb-4 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Need help? View setup instructions
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="p-4 bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-600">
          <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-3">
            {instructions.title}
          </h4>

          <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-600 dark:text-gray-300 mb-3 ml-1">
            {instructions.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>

          {instructions.link && (
            <a
              href={instructions.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline mb-3"
            >
              <ExternalLink className="w-4 h-4" />
              {instructions.linkText}
            </a>
          )}

          {instructions.permissions && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                <span className="font-medium">Required permissions:</span> {instructions.permissions}
              </p>
            </div>
          )}

          {instructions.warning && (
            <div className="mt-2 flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-xs">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <span className="text-amber-800 dark:text-amber-300">{instructions.warning}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Provider Logo component with fallback
export const ProviderLogo: React.FC<{ provider: string }> = ({ provider }) => {
  const config = PROVIDERS[provider];
  const [imageError, setImageError] = useState(false);

  const getInitial = (): string => {
    if (provider === 'openai') return 'O';
    if (provider === 'anthropic') return 'A';
    if (provider === 'bedrock') return 'B';
    if (provider === 'ollama') return 'OL';
    if (provider === 'vllm') return 'V';
    if (provider === 'openai_compatible') return 'OC';
    if (provider === 'sagemaker') return 'SM';
    return provider[0].toUpperCase();
  };

  const getColor = (): string => {
    const colorMap: Record<string, string> = {
      emerald: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
      teal: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
    };
    return colorMap[config?.color] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
  };

  if (imageError || !config?.logo) {
    return (
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg ${getColor()}`}>
        {getInitial()}
      </div>
    );
  }

  return (
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getColor()}`}>
      <img
        src={config.logo}
        alt={config.name}
        className="w-6 h-6"
        onError={() => setImageError(true)}
      />
    </div>
  );
};
